import { useTranslation } from "react-i18next";

// Database values (English only - for queries, reporting, etc.)
export const CANDIDATE_STATUS_VALUES = {
  NEW: 'new',
  LOOKING: 'looking',
  OPEN: 'open', 
  UNAVAILABLE: 'unavailable',
  HIRED: 'hired'
} as const;

type CandidateStatusValue = typeof CANDIDATE_STATUS_VALUES[keyof typeof CANDIDATE_STATUS_VALUES];

// Status configuration with colors and metadata
const CANDIDATE_STATUS_CONFIG = {
  [CANDIDATE_STATUS_VALUES.LOOKING]: {
    color: 'text-green-500',
    bgColor: 'bg-green-100',
    borderColor: 'border-green-200',
    dotColor: 'bg-green-500'
  },
  [CANDIDATE_STATUS_VALUES.OPEN]: {
    color: 'text-orange-400',
    bgColor: 'bg-orange-100', 
    borderColor: 'border-orange-200',
    dotColor: 'bg-orange-400'
  },
  [CANDIDATE_STATUS_VALUES.NEW]: {
    color: 'text-sky-500',
    bgColor: 'bg-sky-100',
    borderColor: 'border-sky-200',
    dotColor: 'bg-sky-500'
  },
  [CANDIDATE_STATUS_VALUES.UNAVAILABLE]: {
    color: 'text-red-500',
    bgColor: 'bg-red-100',
    borderColor: 'border-red-200', 
    dotColor: 'bg-red-500'
  },
  [CANDIDATE_STATUS_VALUES.HIRED]: {
    color: 'text-blue-500',
    bgColor: 'bg-blue-100',
    borderColor: 'border-blue-200',
    dotColor: 'bg-blue-500'
  }
} as const;

// Hook to get localized status display
export const useCandidateStatus = () => {
  const { t } = useTranslation();
  
  const getLocalizedStatus = (status: string | null | undefined): string => {
    if (!status) return t('candidates.status.noStatus', 'No status');
    
    const normalizedStatus = status.toLowerCase();
    return t(`candidates.status.${normalizedStatus}`, status);
  };
  
  const getStatusColor = (status: string | null | undefined): string => {
    if (!status) return 'text-gray-400';
    
    const normalizedStatus = status.toLowerCase();
    return CANDIDATE_STATUS_CONFIG[normalizedStatus as CandidateStatusValue]?.color || 'text-gray-400';
  };
  
  const getStatusDotColor = (status: string | null | undefined): string => {
    if (!status) return 'bg-gray-400';
    
    const normalizedStatus = status.toLowerCase();
    return CANDIDATE_STATUS_CONFIG[normalizedStatus as CandidateStatusValue]?.dotColor || 'bg-gray-400';
  };
  
  const getStatusConfig = (status: string | null | undefined) => {
    if (!status) return CANDIDATE_STATUS_CONFIG[CANDIDATE_STATUS_VALUES.OPEN];
    
    const normalizedStatus = status.toLowerCase();
    return CANDIDATE_STATUS_CONFIG[normalizedStatus as CandidateStatusValue] || CANDIDATE_STATUS_CONFIG[CANDIDATE_STATUS_VALUES.OPEN];
  };
  
  const getStatusOptions = (currentStatus?: string | null) => {
    return Object.values(CANDIDATE_STATUS_VALUES)
      .filter(value => value === CANDIDATE_STATUS_VALUES.NEW ? (!currentStatus || currentStatus === CANDIDATE_STATUS_VALUES.NEW) : true)
      .map(value => ({
        value,
        label: getLocalizedStatus(value)
      }));
  };
  
  return {
    getLocalizedStatus,
    getStatusColor,
    getStatusDotColor,
    getStatusConfig,
    getStatusOptions,
    STATUS_VALUES: CANDIDATE_STATUS_VALUES
  };
};

// Utility function for components that don't use hooks
const getLocalizedStatus = (status: string | null | undefined, t: any): string => {
  if (!status) return t('candidates.status.noStatus', 'No status');
  
  const normalizedStatus = status.toLowerCase();
  return t(`candidates.status.${normalizedStatus}`, status);
};

const getStatusColor = (status: string | null | undefined): string => {
  if (!status) return 'text-gray-400';
  
  const normalizedStatus = status.toLowerCase();
  return CANDIDATE_STATUS_CONFIG[normalizedStatus as CandidateStatusValue]?.color || 'text-gray-400';
};

const getStatusDotColor = (status: string | null | undefined): string => {
  if (!status) return 'bg-gray-400';
  
  const normalizedStatus = status.toLowerCase();
  return CANDIDATE_STATUS_CONFIG[normalizedStatus as CandidateStatusValue]?.dotColor || 'bg-gray-400';
}; 